'use strict';
var App = require('app');
var Consts = require('consts');
var i18n = require('utils/i18n');
var Logger = require('utils/log');
var Misc = require('utils/misc');
var ValueCollection = require('models/values').Collection;

// The Value model mimics the cumulativeValue-struct in data\devices
var CumulativeValue = Backbone.Model.extend({
    defaults: {
        address: '',
        device_ids: [],
        agg_function: Consts.AggregationByName.NONE,
    },

    getMode: function () {
        if (this.get('agg_function') == Consts.AggregationByName.NONE) {
            switch (this.get('address')) {
                case 'WD':
                    return 'weather';
            }
        }
        return 'remote';
    },
});

var getCumulativeValueConstant = function (value) {
    return {
        const_value: value,
        address: '',
        device_ids: [],
        agg_function: Consts.AggregationByName.NONE,
    };
};

var getCumulativeValueSingle = function (deviceID, address) {
    return {
        address: address,
        device_ids: [deviceID],
        agg_function: Consts.AggregationByName.SUM,
    };
};

var getCumulativeValueStatus = function (deviceID) {
    return {
        address: 'status',
        device_ids: [deviceID],
        agg_function: Consts.AggregationByName.NONE,
    };
};

// The rule model mimics the remoteValueRule-struct in data\devices
var RemoteValueRule = Backbone.Model.extend({
    defaults: {
        rank: 1,
        operator: '=',
        values: [new CumulativeValue(), new CumulativeValue(), new CumulativeValue()],
    },

    initialize: function (options) {
        Logger.assert(!!options.values && options.values.length == 3);

        this.set('values', [
            new CumulativeValue(options.values[0]),
            new CumulativeValue(options.values[1]),
            new CumulativeValue(options.values[2]),
        ]);
    },

    getMode: function () {
        return this.get('values')[2].getMode();
    },

    getSource: function () {
        var values = this.get('values');
        Logger.assert(!!values && values.length == 3);

        if (!['status', 'WD', ''].includes(values[2].get('address'))) {
            return values[2];
        }

        for (var idx = 0; idx < 2; idx++) {
            // rule depending on the status of a device? -> get the the value containing that device
            if (values[idx].get('address') === 'status') {
                return values[idx];
            }
            // using weather data? -> no source (e.g "device") is involved
            if (values[idx].get('address') === 'WD') {
                return undefined;
            }
        }

        return undefined;
    },

    getSourceAddress: function () {
        return this.getSource().get('address') || '';
    },

    // returns the id of the device providing the data of the rule
    // and undefined if the source is not a device (weather data for example)
    getSourceDeviceID: function () {
        var value = this.getSource();
        if (value === undefined) {
            return undefined;
        }

        var deviceIDs = value.get('device_ids');
        // todo: This function has to be changed, as soon as rules with multiple devices as a source are possible
        //       In that case, the whole design needs rethinking
        Logger.assert(deviceIDs.length <= 1);

        if (deviceIDs.length === 0) {
            return undefined;
        }

        Logger.assert(!isNaN(parseInt(deviceIDs[0])));
        return deviceIDs[0];
    },
});

var RemoteValueTransfer = Backbone.Model.extend({
    defaults: {
        refresh_rate: 900,
    },

    // override the parse function, because in the backend we are returning not only the model,
    // but also something else and by default marionette is expecting only the model
    parse: function (response) {
        return response.remote_value_transfer;
    },

    url: function () {
        return '/api/v1/devices/' + this.get('device_id') + '/transfer/' + this.get('id');
    },

    valid: function () {
        return !!this.get('rules');
    },

    setRules: function (rules) {
        if (!rules) {
            return;
        }

        for (var idx = 0; idx < rules.length; idx++) {
            rules[idx] = new RemoteValueRule(rules[idx]);
        }
        this.set('rules', rules);
    },

    addRules: function (rules) {
        var existingRules = this.get('rules') || [];
        for (var idx = 0; idx < rules.length; idx++) {
            existingRules.push(new RemoteValueRule(rules[idx]));
        }
        this.set('rules', rules);
    },

    // create only one rule with no logic (=always true)
    setRuleSimpleTransfer: function (address, device_ids, rank) {
        rank = rank || 1;
        // no address, device_ids or agg_function, value comes from the weather-server
        this.setRules([
            {
                rank: rank,
                values: [
                    {},
                    {},
                    {
                        address: address,
                        device_ids: device_ids,
                        agg_function: address == 'WD' ? Consts.AggregationByName.NONE : Consts.AggregationByName.SUM,
                    },
                ],
            },
        ]);
    },

    // the rule created fetches the value from sourceDevice if it is online and returns the offlineValue otherwise
    setRuleOfflineFallbackValue: function (sourceDeviceID, address, offlineValue) {
        sourceDeviceID = parseInt(sourceDeviceID);
        offlineValue = parseInt(offlineValue);

        Logger.assert(!isNaN(sourceDeviceID));
        Logger.assert(!isNaN(offlineValue));
        Logger.assert(typeof address === 'string');

        this._setOnlineOfflineRule(sourceDeviceID, address, offlineValue, undefined);
    },

    // this rule returns onlineValue if the device is online and the offlineValue otherwise
    setRuleOfflineOnlineValue: function (sourceDeviceID, offlineValue, onlineValue) {
        sourceDeviceID = parseInt(sourceDeviceID);
        offlineValue = parseInt(offlineValue);
        onlineValue = parseInt(onlineValue);

        Logger.assert(!isNaN(sourceDeviceID));
        Logger.assert(!isNaN(offlineValue));
        Logger.assert(!isNaN(onlineValue));

        this._setOnlineOfflineRule(sourceDeviceID, undefined, offlineValue, onlineValue);
    },

    // only for internal use
    _setOnlineOfflineRule: function (deviceID, address, offlineValue, onlineValue) {
        var comulativeValueOnline = getCumulativeValueSingle(deviceID, address);
        if (onlineValue != undefined) {
            comulativeValueOnline = getCumulativeValueConstant(parseInt(onlineValue));
        }
        this.setRules(
            // no address, device_ids or agg_function, value comes from the weather-server
            [
                {
                    rank: 1,
                    operator: '>=',
                    values: [
                        // left value
                        getCumulativeValueStatus(deviceID),
                        // right value
                        getCumulativeValueConstant(0),
                        // write value
                        comulativeValueOnline,
                    ],
                },
                {
                    rank: 2,
                    operator: '<',
                    values: [
                        // left value
                        getCumulativeValueStatus(deviceID),
                        // right value
                        getCumulativeValueConstant(0),
                        // write value
                        getCumulativeValueConstant(parseInt(offlineValue)),
                    ],
                },
            ]
        );
    },

    getFallbackValues: function () {
        var rules = this.get('rules');
        // todo: if more than two rules are possible, this code has to be changed
        Logger.assert(rules.length <= 2);
        if (rules.length !== 2) {
            return;
        }

        var fallbackValues = {};
        _.each(rules, function (r) {
            var operator = r.get('operator');
            var writeAddress = r.get('values')[2].get('address');
            var writeValue = r.get('values')[2].get('const_value');

            var leftValueIsStatus = r.get('values')[0].get('address') === 'status';
            var rightValueIsConstant = r.get('values')[1].get('agg_function') === Consts.AggregationByName.NONE;
            var writeValueIsConstant = writeAddress === '';

            if (operator == '>=' && leftValueIsStatus && rightValueIsConstant && writeValueIsConstant) {
                fallbackValues.online = writeValue;
            }

            if (operator == '<' && leftValueIsStatus && rightValueIsConstant && writeValueIsConstant) {
                fallbackValues.offline = writeValue;
            }
        });

        return fallbackValues;
    },

    getActiveRule: function () {
        var activeRuleIdx = undefined;
        var rulesSortedByRank = _.sortBy(this.get('rules'), function (r) {
            return r.get('rank');
        });

        _.each(rulesSortedByRank, function (r, idx) {
            var leftValue = r.get('values')[0].get('const_value');
            var rightValue = r.get('values')[1].get('const_value');

            var operator = r.get('operator') === '=' ? '==' : r.get('operator');
            if (activeRuleIdx === undefined && Misc.evaluateToBool(leftValue, operator, rightValue)) {
                activeRuleIdx = idx;
            }
        });

        // It should not be possible to define a ruleset where all rules evaluate to false in any situations.
        Logger.assert(activeRuleIdx != undefined);

        return activeRuleIdx === undefined ? undefined : this.get('rules')[activeRuleIdx];
    },

    getMode: function () {
        var rule = this.getActiveRule();
        return rule === undefined ? undefined : rule.getMode();
    },
});

// We are creating a "pseudo class" here. This function serves as a constructer. Now we can do:
// var x = new Value(...)
// Below we define the methods of the Value object
function Value(valueModel, formattedValue) {
    if (valueModel) {
        this.changeFormat(valueModel);
    }

    if (formattedValue) {
        this.rawValue = this.set(formattedValue);
    }
}

Value.prototype.setFormatted = function (formattedValue) {
    // reset the value
    if (formattedValue === undefined || formattedValue === '') {
        this.type = undefined;
        return;
    }
    Logger.assert(this.type, this.format);

    var rawValue = Misc.getRawValue(formattedValue, this.type, this.format);
    this.rawValue = Misc.clamp(rawValue, this.min, this.max);
};

Value.prototype.setRaw = function (value) {
    this.rawValue = value;
};

Value.prototype.changeFormat = function (valueModel) {
    var vm = valueModel.attributes;

    this.cid = valueModel.cid;
    this.type = vm.type;
    this.nr = vm.nr;
    this.format = vm.format;
    this.min = Misc.parseIntDef(vm.min, -Infinity);
    this.max = Misc.parseIntDef(vm.max, Infinity);

    if (vm.type === 'MBC') {
        this.min = 0;
        this.max = 1;
    }

    if (this.min === this.max) {
        this.min = -Infinity;
        this.max = Infinity;
    }

    // invalidate value
    this.rawValue = undefined;
};

Value.prototype.getRaw = function () {
    return this.rawValue;
};

Value.prototype.getFormatted = function () {
    if (this.type === undefined) {
        return '';
    }
    return (Misc.getValueDisplayInfos(this.rawValue, this.type, this.format) || {}).value;
};

function FallbackValues(options) {
    this.values = {};
    this.targetFallbackDisabled = options.targetFallbackDisabled;
    // initialize fallback values from device
    if (options.values) {
        var registers = options.values.registers;
        for (var idx = 3; idx < registers.length; idx += 3) {
            // unused value? -> skip
            if (registers[idx].value === 0) {
                continue;
            }

            var type = registers[idx].value === 1 ? 'MBR' : 'MBC';
            var address = registers[idx + 1].value;
            var value = registers[idx + 2].value;
            var format = _.filter(options.formats, function (format) {
                return format.attributes.nr === address && format.attributes.type === type;
            });
            if (format.length > 0) {
                this.addRaw(format[0].cid, value, format[0]);
            } else {
                // We cannot initialize fallback values, if the format is missing
                Logger.assert(false);
                this.targetFallbackDisabled = true;
                return undefined;
            }
        }
    } else {
        this.values = { dummy: new Value() };
    }
    this.formats = new ValueCollection(options.formats);
    this.targetFormat = options.targetFormat;
}

FallbackValues.prototype.addFormatted = function (key, format, formattedValue) {
    this.values[key] = new Value(format, formattedValue);
};

FallbackValues.prototype.addRaw = function (key, rawValue, format) {
    var value = new Value(format);
    value.setRaw(rawValue);
    this.values[key] = value;
};

FallbackValues.prototype.setFormatted = function (key, formattedValue) {
    this.values[key].setFormatted(formattedValue);
};

FallbackValues.prototype.getValue = function (key) {
    return this.values[key];
};

FallbackValues.prototype.getFormatted = function (key) {
    return this.values[key].getFormatted();
};

FallbackValues.prototype.getRawValues = function () {
    return _.mapObject(this.values, function (value, _key) {
        return value.getRaw();
    });
};

// getTargetValues gets all values target values except the online, offline and dummy value
FallbackValues.prototype.getTargetValues = function () {
    // todo: When the timeout can be set by the user, use the value from the ui here.
    //       backend does not use this value yet
    var config = { timeout_in_minutes: 10, data: [] };
    _.each(
        this.values,
        function (value, key) {
            if (IsTargetValue(key)) {
                config.data.push({
                    address: value.nr,
                    type: value.type,
                    value: value.getRaw(),
                });
            }
        },
        this
    );
    return config;
};

FallbackValues.prototype.remove = function (key) {
    // Do not allow removal of the empty entry
    if (key === 'dummy') {
        return;
    }
    delete this.values[key];
};

FallbackValues.prototype.hasValue = function (key) {
    return this.values[key] != undefined;
};

FallbackValues.prototype.hasFormats = function () {
    return this.formats != undefined;
};

FallbackValues.prototype.getNewKey = function () {
    var max = 0;
    _.each(this.values, function (_val, key) {
        if (key.startsWith('target')) {
            max = Math.max(max, parseInt(key.split('-')[1]));
        }
    });
    return max + 1;
};

FallbackValues.prototype.changeFormat = function (key, formatID) {
    // changing dummy entry? -> add new entry
    if (key === 'dummy') {
        this.addRaw('target-' + this.getNewKey(), 0, this.formats.get(formatID));
        return;
    }
    this.values[key].changeFormat(this.formats.get(formatID));
};

FallbackValues.prototype.loadFormats = function (formats) {
    this.formats = formats;
};

FallbackValues.prototype.invalidateTarget = function () {
    _.each(
        this.values,
        function (_value, key) {
            if (IsTargetValue(key)) {
                delete this.values[key];
            }
        },
        this
    );
};

function IsTargetValue(key) {
    return !['offline', 'online', 'dummy'].includes(key);
}

var RemoteValueState = Backbone.Model.extend({
    offlineDetectionID: -1,

    initialize: function (options) {
        this.set('rvt', options.rvt);
        this.set('targetFallbackDisabled', options.targetFallbackDisabled);
        // initialize target fallbackvalues (requested from the gateway)
        this.set(
            'fallbackValues',
            new FallbackValues({
                formats: options.formats,
                targetFormat: options.model,
                values: options.fallbackValues,
                targetFallbackDisabled: options.targetFallbackDisabled,
            })
        );

        if (!this.isValid()) {
            return;
        }

        this.setSourceDeviceID();

        // add remote fallbackvalues (offline/online)
        var fbv = this.get('rvt').getFallbackValues();
        if (fbv) {
            _.each(
                fbv,
                function (value, key) {
                    this.get('fallbackValues').addRaw(key, value, options.model);
                },
                this
            );
        }
    },

    initializeValues: function (sourceFallbackEnabled) {
        var fbv = this.get('fallbackValues');
        fbv.remove('offline');
        fbv.remove('online');
        if (sourceFallbackEnabled || this.offlineDetectionEnabled()) {
            fbv.addRaw('offline', 0, fbv.targetFormat);
        }

        if (this.offlineDetectionEnabled()) {
            var initVal = fbv.targetFormat.get('type') === 'MBC' ? 1 : 0;
            fbv.addRaw('online', initVal, fbv.targetFormat);
        }
    },

    invalidateSouceValueID: function () {
        this.set('sourceValueID', undefined);
        this.get('fallbackValues').remove('offline');
        this.get('fallbackValues').remove('online');
    },

    isValid: function () {
        return this.get('rvt') && this.get('rvt').valid() && this.get('rvt').getMode();
    },

    getMode: function () {
        return this.get('rvt').getMode();
    },

    setSourceDeviceID: function (deviceID) {
        if (deviceID) {
            deviceID = parseInt(deviceID);
            Logger.assert(!isNaN(deviceID));
            this.set('sourceDeviceID', deviceID);
            return;
        }

        this.set('sourceDeviceID', this.get('rvt').get('rules')[0].getSourceDeviceID());
    },

    setSourceValueID: function (valueID) {
        this.set('sourceValueID', valueID);
        return;
    },

    setSourceValueIDFromRule: function (values) {
        var address = this.get('rvt').get('rules')[0].getSourceAddress();
        switch (address) {
            case '':
                // no address -> must be a rule depending on the status setting a constant value
                // todo: Extend this case, when constant values can be set for a rule outside of status dependend rules.
                Logger.assert(this.get('rvt').get('rules')[0].get('values')[0].get('address') === 'status');
                this.set('sourceValueID', this.offlineDetectionID);
                return;
            case 'status':
                this.set('sourceValueID', this.offlineDetectionID);
                return;
        }

        Logger.assert(values);
        this.set(
            'sourceValueID',
            values.getByAddress(parseInt(address.substring(4)), address.substring(0, 3)).get('id')
        );
    },

    offlineDetectionEnabled: function () {
        return this.get('sourceValueID') === this.offlineDetectionID;
    },

    hasOfflineValue: function () {
        return this.get('fallbackValues').hasValue('offline');
    },

    canToggleFallbackValues: function () {
        // disable fallbackValues for time and date
        if (
            (this.attributes.model.attributes.nr == 100 && this.attributes.model.attributes.type == 'MBR') ||
            (this.attributes.model.attributes.nr == 101 && this.attributes.model.attributes.type == 'MBR')
        ) {
            return false;
        }

        return this.get('sourceValueID') && this.get('sourceValueID') !== this.offlineDetectionID;
    },

    getDisplayState: function (values) {
        var hasValues = !!values && !!values.models && values.models.length > 0;

        return {
            sourceDeviceID: this.get('sourceDeviceID'),
            values: hasValues ? values.models : undefined,
            noValues: !hasValues,
            selectedValueID: this.get('sourceValueID'),
            fallbackUnit: this.get('unit'),
            offlineDetectionEnabled: this.offlineDetectionEnabled(),
            canToggleFallbackValues: this.canToggleFallbackValues(),
            allValues: this.get('fallbackValues').values,
            rawValues: this.get('fallbackValues').getRawValues(),
            targetFallbackDisabled: this.get('fallbackValues').targetFallbackDisabled,
            disablingReason: this.get('disablingReason'),
        };
    },
});

var API = {
    getRemoteValueTransfer: function (address, device_id, valueModel) {
        var rvt = new RemoteValueTransfer({ device_id: device_id, id: address });
        var defer = $.Deferred();

        rvt.fetch({
            success: function (model, response) {
                if (response === 'noRules') {
                    defer.resolve({
                        success: true,
                        rvt: rvt,
                    });
                    return;
                }

                Logger.assert(valueModel.get('transfer_mode') != Consts.TransferMode.None);
                if (!response.has_access) {
                    defer.resolve({
                        success: false,
                        type: 'error',
                        header: i18n.TR('Fehler'),
                        message: i18n.TR('Ihnen fehlen Rechte für mindestens ein Quellgerät des Fremdwertbezuges.'),
                    });
                    return;
                }

                rvt.setRules(model.get('rules'));
                defer.resolve({
                    success: true,
                    rvt: rvt,
                });
                return;
            },
            error: function (s) {
                defer.resolve({
                    success: false,
                    type: 'error',
                    header: i18n.TR('Fehler'),
                    message: i18n.TR('Fehler beim Laden der Regeln'),
                });
            },
        });
        return defer.promise();
    },
};

App.reqres.setHandler('device:remote_value_transfer', function (address, device_id, valueModel) {
    return API.getRemoteValueTransfer(address, device_id, valueModel);
});

module.exports = {
    RemoteValueState: RemoteValueState,
    RemoteValueTransfer: RemoteValueTransfer,
    RemoteValueRule: RemoteValueRule,
    CumulativeValue: CumulativeValue,
    IsTargetValue: IsTargetValue,
};
